import React, { useRef, useState } from 'react';
import {
    ConfigProvider,
    theme,
    Input,
    Select,
    Button,
    Checkbox,
    Radio,
    Upload,
    Tooltip,
    Modal,
} from 'antd';
import {
    ChevronDown,
    Search,
    X,
    Plus,
    UploadCloud,
    GripVertical,
    Layers,
    User,
    HelpCircle,
    Moon,
    Sun,
    ChevronRight,
} from 'lucide-react';

import DocumentSign, { DocumentSignRef } from './DocumentSign';

interface ModalDigitalSignProps {
    open: boolean,
    onClose: () => void
}

const { TextArea } = Input;
const { Dragger } = Upload;


export function ModalDigitalSignature({ open, onClose }: ModalDigitalSignProps) {
    const [isDarkMode, setIsDarkMode] = useState(false);

    const signRef = useRef<DocumentSignRef>(null);

    const [expandedSections, setExpandedSections] = useState({
        general: true,
        join: true,
        sign: true,
        attached: true,
    });

    // --- Step (Tạo hồ sơ ký / Cấu hình vị trí / Trình ký) ---
    const steps = [
        { id: 1, label: 'Tạo hồ sơ ký' },
        { id: 2, label: 'Cấu hình vị trí' },
        { id: 3, label: 'Trình ký' },
    ];
    const [currentStep, setCurrentStep] = useState(1);

    // --- Thông tin ---
    const [lockAfterSign, setLockAfterSign] = useState(false);
    const [lockPosition, setLockPosition] = useState(false);

    // --- Người tham gia ---
    const createSigner = () => ({
        id: Date.now() + Math.random(),
        participant: "",
        signerName: "",
        email: "",
        role: "signer",
        dueDate: "",
        dueTime: "",
        requireNote: true,
    });

    const [signSteps, setSignSteps] = useState([
        {
            id: 1,
            mode: "free",
            signers: [createSigner()],
        },
    ]);

    const handleAddStep = () => {
        setSignSteps((prev) => [
            ...prev,
            {
                id: prev.length + 1,
                mode: "free",
                signers: [createSigner()],
            },
        ]);
    };

    const handleAddSigner = (stepId: number) => {
        setSignSteps((prev) =>
            prev.map((step) =>
                step.id === stepId
                    ? { ...step, signers: [...step.signers, createSigner()] }
                    : step
            )
        );
    };

    const handleRemoveSigner = (stepId: number, signerId: number) => {
        setSignSteps((prev) =>
            prev.map((step) =>
                step.id === stepId
                    ? {
                        ...step,
                        signers: step.signers.filter((s) => s.id !== signerId),
                    }
                    : step
            )
        );
    };

    const updateSigner = (
        stepId: number,
        signerId: number,
        key: string,
        value: any
    ) => {
        setSignSteps((prev) =>
            prev.map((step) =>
                step.id === stepId
                    ? {
                        ...step,
                        signers: step.signers.map((s) =>
                            s.id === signerId ? { ...s, [key]: value } : s
                        ),
                    }
                    : step
            )
        );
    };

    const updateStepMode = (stepId: number, mode: string) => {
        setSignSteps((prev) =>
            prev.map((step) => (step.id === stepId ? { ...step, mode } : step))
        );
    };

    const toggleSection = (key) => {
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const SectionHeader = ({ title, sectionKey }) => (
        <div
            onClick={() => toggleSection(sectionKey)}
            className="flex items-center gap-2 text-[#3e91ab] font-medium text-sm mb-4 mt-8 first:mt-0 cursor-pointer hover:opacity-80 transition-opacity select-none"
        >
            {expandedSections[sectionKey] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            <span>{title}</span>
        </div>
    );

    const handleSaveStep2 = () => {
        signRef.current?.handleSaveAll()
        setCurrentStep(3)
    }

    const roleOptions = [{ value: 'signer', label: 'Người ký' }];

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={{
                xs: '100%',
                sm: '90%',
                md: '80%',
                lg: '70%',
                xl: '60%',
                xxl: '50%',
            }}
            title={
                <div className="relative flex items-center justify-center px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
                    <div className="flex items-center gap-3 sm:gap-6 overflow-x-auto no-scrollbar">
                        {steps.map((step, idx) => {
                            const active = step.id === currentStep;
                            const done = step.id < currentStep;
                            return (
                                <div key={step.id} className="flex items-center gap-2 sm:gap-3 shrink-0">
                                    <div className="flex items-center gap-2">
                                        <span
                                            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold shrink-0
                                                        ${active
                                                    ? 'bg-[#3E91AB] text-white'
                                                    : done
                                                        ? 'bg-[#3E91AB] text-white'
                                                        : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}
                                        >
                                            {step.id}
                                        </span>
                                        <span
                                            className={`text-sm whitespace-nowrap ${active ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-400 dark:text-gray-500'}`}
                                        >
                                            {step.label}
                                        </span>
                                    </div>
                                    {idx < steps.length - 1 && (
                                        <span className="w-6 sm:w-10 h-px bg-gray-300 dark:bg-gray-600" />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            }
            footer={[
                currentStep === 1 ? (
                    <div key='step1' className="flex gap-5 justify-end" style={{ flexWrap: 'wrap' }}>
                        <Button
                            onClick={onClose}
                            className="px-5 py-2.5 dark:text-white font-medium rounded-md text-xs transition-colors"
                            style={{ background: 'transparent' }}
                        >
                            HỦY BỎ
                        </Button>
                        <Button
                            onClick={() => setCurrentStep(2)}
                            type="primary"
                            className="px-5 py-2.5 text-white font-medium rounded-md text-xs transition-colors"
                        >
                            CẤU HÌNH
                        </Button>
                    </div>
                ): currentStep === 2 ? (
                   <div key='step2' className="flex gap-5 justify-end" style={{ flexWrap: 'wrap' }}>
                        <Button
                            onClick={onClose}
                            className="px-5 py-2.5 dark:text-white font-medium rounded-md text-xs transition-colors"
                            style={{ background: 'transparent' }}
                        >
                            THOÁT VÀ LƯU
                        </Button>
                        <Button
                            onClick={handleSaveStep2}
                            type="primary"
                            className="px-5 py-2.5 text-white font-medium rounded-md text-xs transition-colors"
                        >
                            GỬI HỒ SƠ KÝ
                        </Button>
                    </div>
                ) : (
                    <div key='step3' className="flex gap-5 justify-end" style={{ flexWrap: 'wrap' }}>
                        <Button
                            onClick={onClose}
                            className="px-5 py-2.5 dark:text-white font-medium rounded-md text-xs transition-colors"
                            style={{ background: 'transparent' }}
                        >
                            THOÁT
                        </Button>
                        {/* <Button
                            onClick={() => setCurrentStep(3)}
                            type="primary"
                            className="px-5 py-2.5 text-white font-medium rounded-md text-xs transition-colors"
                        >
                            GỬI HỒ SƠ KÝ
                        </Button> */}
                    </div>
                )
            ]}
        >
            {/* <ConfigProvider theme={antdThemeConfig}>
                <div className={isDarkMode ? 'dark' : ''}>
                </div>
            </ConfigProvider> */}
            {/* <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 p-2 sm:p-6 transition-colors duration-300">
                    </div> */}
            <div className="w-full bg-white dark:bg-gray-800 flex flex-col">
                {/* ================= BODY (scrollable) ================= */}
                {currentStep === 1 ? (
                    <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">

                        {/* --- Thông tin --- */}
                        <SectionHeader title="Thông tin" sectionKey="general" />
                        {expandedSections.general && (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4 mb-4">
                                    <div>
                                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
                                            Mã hồ sơ
                                            <Tooltip title="Mã hồ sơ được sinh tự động">
                                                <HelpCircle size={12} className="text-[#3E91AB] cursor-pointer" />
                                            </Tooltip>
                                        </label>
                                        <Input defaultValue="3444" disabled />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                                            Tên hồ sơ<span className="text-red-500"> *</span>
                                        </label>
                                        <Input defaultValue="Mua hệ thống vận hành nội bộ" />
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2 mb-4">
                                    <Checkbox
                                        checked={lockAfterSign}
                                        onChange={(e) => setLockAfterSign(e.target.checked)}
                                        className="text-sm text-gray-700 dark:text-gray-300"
                                    >
                                        Không cho sửa hồ sơ sau khi đã có người ký/xác nhận (chỉ mở lại khi bị từ chối)
                                    </Checkbox>
                                    <Checkbox
                                        checked={lockPosition}
                                        onChange={(e) => setLockPosition(e.target.checked)}
                                        className="text-sm text-gray-700 dark:text-gray-300"
                                    >
                                        Khóa vị trí, kích thước chữ ký và bút phê khi ký
                                    </Checkbox>
                                </div>

                                <div className="mb-8">
                                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Mô tả</label>
                                    <TextArea
                                        rows={2}
                                        defaultValue="Gửi hồ sơ ký số cho Phiếu chi - Mua hệ thống vận hành nội bộ"
                                    />
                                </div>
                            </>
                        )}

                        {/* --- Người tham gia --- */}
                        <SectionHeader title="Người tham gia" sectionKey="join" />
                        {expandedSections.join && (
                            <>
                                <div className="flex items-center justify-end mb-4 flex-wrap gap-2">
                                    <div className="flex items-center gap-2">
                                        <Button size="small" icon={<UploadCloud size={14} />}>
                                            Lưu cấu hình
                                        </Button>
                                        <Button size="small">Mở cấu hình đã lưu</Button>
                                    </div>
                                </div>

                                {signSteps.map((step) => (
                                    <div key={step.id} className="mb-4 pb-4 border-b ">
                                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                            <div className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100">
                                                <span>Bước {step.id}</span>
                                                <button className="text-gray-400 hover:text-[#3E91AB]">
                                                    <Layers size={14} />
                                                </button>
                                            </div>

                                            <Radio.Group
                                                value={step.mode}
                                                onChange={(e) => updateStepMode(step.id, e.target.value)}
                                                className="flex items-center gap-4"
                                            >
                                                <Radio value="free">Ký tự do</Radio>
                                                <Radio value="order">Ký theo thứ tự</Radio>
                                            </Radio.Group>
                                        </div>

                                        <div className="mb-2">
                                            <div className="hidden md:grid grid-cols-[24px_1.4fr_1.2fr_1.4fr_1fr_0.8fr_0.8fr_1fr_24px] gap-2 text-[11px] font-medium text-gray-500 px-1 mb-2">
                                                <span />
                                                <span>Người ký</span>
                                                <span>Tên người ký</span>
                                                <span>Email người ký</span>
                                                <span>Vai trò</span>
                                                <span>Hạn ký duyệt</span>
                                                <span></span>
                                                <span>Bút phê</span>
                                                <span />
                                            </div>

                                            {step.signers.map((signer) => (
                                                <div
                                                    key={signer.id}
                                                    className="grid grid-cols-1 md:grid-cols-[24px_1.4fr_1.2fr_1.4fr_1fr_0.8fr_0.8fr_1fr_24px] gap-2 mb-3 items-center border md:border-0 rounded-lg p-3 md:p-0"
                                                >
                                                    <span className="hidden md:flex justify-center text-gray-300">
                                                        <GripVertical size={16} />
                                                    </span>

                                                    <Input
                                                        placeholder="Chọn nhân sự"
                                                        value={signer.participant}
                                                        onChange={(e) =>
                                                            updateSigner(step.id, signer.id, "participant", e.target.value)
                                                        }
                                                    />

                                                    <Input
                                                        placeholder="Tên người ký"
                                                        value={signer.signerName}
                                                        onChange={(e) =>
                                                            updateSigner(step.id, signer.id, "signerName", e.target.value)
                                                        }
                                                    />

                                                    <Input
                                                        placeholder="Email"
                                                        value={signer.email}
                                                        onChange={(e) =>
                                                            updateSigner(step.id, signer.id, "email", e.target.value)
                                                        }
                                                    />

                                                    <Select
                                                        value={signer.role}
                                                        options={roleOptions}
                                                        onChange={(val) => updateSigner(step.id, signer.id, "role", val)}
                                                    />

                                                    <Input
                                                        placeholder="Ngày"
                                                        value={signer.dueDate}
                                                        onChange={(e) =>
                                                            updateSigner(step.id, signer.id, "dueDate", e.target.value)
                                                        }
                                                    />

                                                    <Input
                                                        placeholder="Giờ"
                                                        value={signer.dueTime}
                                                        onChange={(e) =>
                                                            updateSigner(step.id, signer.id, "dueTime", e.target.value)
                                                        }
                                                    />

                                                    <Select
                                                        value={signer.requireNote}
                                                        options={[
                                                            { value: true, label: "Được bút phê" },
                                                            { value: false, label: "Không bút phê" },
                                                        ]}
                                                        onChange={(val) =>
                                                            updateSigner(step.id, signer.id, "requireNote", val)
                                                        }
                                                    />

                                                    <div className="flex justify-end md:justify-center">
                                                        <X
                                                            size={18}
                                                            className="text-gray-400 cursor-pointer hover:text-red-500"
                                                            onClick={() => handleRemoveSigner(step.id, signer.id)}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <button
                                            onClick={() => handleAddSigner(step.id)}
                                            className="flex items-center justify-center w-6 h-6 rounded-full border border-[#3E91AB] text-[#3E91AB] hover:bg-[#3E91AB] hover:text-white transition-colors"
                                        >
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                ))}

                                <div className="mb-8">
                                    <Button
                                        type="text"
                                        onClick={handleAddStep}
                                        className="!text-[#3E91AB] !px-0 flex items-center gap-1"
                                        icon={<Layers size={14} />}
                                    >
                                        Thêm bước ký
                                    </Button>
                                </div>
                            </>
                        )}

                        {/* --- File ký --- */}
                        <SectionHeader title="File ký" sectionKey="sign" />
                        {expandedSections.sign && (
                            <UploadBox isDarkMode={isDarkMode} />
                        )}

                        {/* --- File đính kèm --- */}
                        <SectionHeader title="File đính kèm" sectionKey='attached' />
                        {expandedSections.attached && (
                            <UploadBox isDarkMode={isDarkMode} className="mb-2" />
                        )}
                    </div>
                ): currentStep === 2 ? (
                    <DocumentSign ref={signRef}/>
                ):(
                    <div>step 3</div>
                )}
            </div>
        </Modal>
    );
}

// function SectionHeader({ title }) {
//     return (
//         <div className="flex items-center gap-2 text-[#3E91AB] font-medium text-sm mb-4 mt-2 first:mt-0 select-none">
//             <ChevronDown size={18} />
//             <span>{title}</span>
//         </div>
//     );
// }

function UploadBox({ isDarkMode, className = '' }) {
    return (
        <div className={`mb-8 ${className}`}>
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Upload</label>
            <Dragger
                className="bg-transparent"
                style={{ padding: '24px 16px', border: `1px dashed ${isDarkMode ? '#4b5563' : '#d1d5db'}` }}
                showUploadList={false}
            >
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#3E91AB]/10 flex items-center justify-center shrink-0">
                        <UploadCloud size={20} className="text-[#3E91AB]" />
                    </div>
                    <div className="text-center sm:text-left">
                        <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
                            Kéo thả file vào đây để tải lên hoặc
                        </p>
                        <Button type="primary" size="small" style={{ backgroundColor: '#3E91AB' }}>
                            <span className="text-sm">Chọn từ máy</span>
                        </Button>
                    </div>
                </div>
            </Dragger>
        </div>
    );
}